<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE'? $t('Create Category'): $t('Edit Category') }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div class="offcanvas-body p-0" v-else>
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('Category Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-md-6">
            <label for="name" class="form-label">
              {{ $t('Category Name (TH)') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="name"
                type="text"
                v-model.trim="v$.form.name.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter category name') + '...'"
              >
              <div v-for="error of v$.form.name.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label for="name_en" class="form-label">
              {{ $t('Category Name (EN)') }}
            </label>
            <div class="input-group-sm">
              <input
                id="name_en"
                type="text"
                v-model.trim="v$.form.name_en.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter category name in English') + '...'"
              >
              <div class="form-text text-muted">{{ $t('Leave blank to show the Thai name.') }}</div>
              <div v-for="error of v$.form.name_en.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label for="description" class="form-label">{{ $t('Description (TH)') }}</label>
            <vue-editor
              v-model="v$.form.description.$model"
              :editor-toolbar="editor.toolbar"
              :placeholder="$t('Enter description') + '...'"
              :class="v$.form.description.$error ? 'is-invalid' : ''"
            />
            <div v-for="error of v$.form.description.$errors" class="invalid-feedback" :key="error.$uid">
              {{ error.$message }}
            </div>
          </div>
          <div class="col-md-6">
            <label for="description_en" class="form-label">{{ $t('Description (EN)') }}</label>
            <vue-editor
              v-model="v$.form.description_en.$model"
              :editor-toolbar="editor.toolbar"
              :placeholder="$t('Enter description in English') + '...'"
              :class="v$.form.description_en.$error ? 'is-invalid' : ''"
            />
            <div class="form-text text-muted">{{ $t('Leave blank to show the Thai description.') }}</div>
            <div v-for="error of v$.form.description_en.$errors" class="invalid-feedback" :key="error.$uid">
              {{ error.$message }}
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label" for="flexSwitchCheckStatus">
              {{ $t('Status') }}
            </label>
            <div class="form-check form-switch">
              <input
                class="form-check-input shadow-none"
                type="checkbox"
                id="flexSwitchCheckStatus"
                v-model.trim="v$.form.is_active.$model"
              >
              <label class="form-check-label" for="flexSwitchCheckStatus">
                {{ $t('Active') }}
              </label>
            </div>
            <!-- ปิดหมวดที่มีสินค้าอยู่ = สินค้าทั้งกลุ่มหายจากเมนูลูกค้าพร้อมกัน จึงต้องบอกให้เห็นก่อนกดบันทึก -->
            <div
              v-if="!form.is_active && productsCount > 0"
              class="alert alert-warning py-1 px-2 mt-2 mb-0 small"
            >
              <i class="fa-solid fa-triangle-exclamation me-1" />
              {{ $t('Turning this off hides its {count} product(s) from the customer menu.', { count: productsCount }) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { ProductCategories } from '@/services/endpoint/product-categories.service';
import { VueEditor } from "vue3-editor";

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      productsCount: 0,
      // ชุดปุ่มเดียวกับฟอร์มสินค้า — คำอธิบายทั้งสองที่เก็บเป็น HTML เหมือนกัน
      editor: {
        toolbar: [
          [{
            header: [false, 1, 2, 3, 4, 5, 6]
          }],
          ["bold", "italic", "underline", "strike"],
          [{
            align: ""
          }, {
            align: "center"
          }, {
            align: "right"
          }, {
            align: "justify"
          }],
          [{
            list: "ordered"
          }, {
            list: "bullet"
          }],
          [{
            color: []
          }],
        ],
      },
      form: {
        name: '',
        name_en: '',
        description: '',
        description_en: '',
        is_active: true
      }
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    AccessDenied,
    VueEditor
  },
  methods: {
    async getCreateData() {
      await ProductCategories.getCreate().then(res => {
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    async getUpdateData() {
      await ProductCategories.getUpdate(this.id).then(res => {
        this.form = {
          name: res.data.data.name,
          name_en: res.data.data.name_en,
          description: res.data.data.description,
          description_en: res.data.data.description_en,
          is_active: res.data.data.is_active
        };
        this.productsCount = res.data.data.products_count;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    /**
     * vue-editor คืน '<p><br></p>' ตอนที่ผู้ใช้ลบข้อความจนหมด ซึ่งเป็น string ที่ truthy
     * ถ้าปล่อยไปตรง ๆ = ล้างคำอธิบายแล้วยังเก็บ tag เปล่าไว้ใน DB → ถือว่าว่างจริงแล้วเก็บ null
     */
    emptyHtmlToNull(value) {
      if (!value) return null;
      const text = String(value).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      return text ? value : null;
    },
    setParams() {
      // ส่ง key ครบทุกตัวเสมอ — ฟิลด์ optional ที่ล้างค่าต้องเป็น null ไม่ใช่หายไปจาก payload
      // (หายไป = BE มองว่าไม่ได้แก้ = ค่าเดิมค้าง) และ API เป็น strict whitelist ห้ามมี key เกิน
      return {
        name: this.form.name,
        name_en: this.form.name_en || null,
        description: this.emptyHtmlToNull(this.form.description),
        description_en: this.emptyHtmlToNull(this.form.description_en),
        is_active: this.form.is_active
      };
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await ProductCategories.create(this.setParams()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Category!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(err?.response?.data?.message);
        }
        this.loadingSave = false;
      });
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await ProductCategories.update(this.id, this.setParams()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Category!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(err?.response?.data?.message);
        }
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas', async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  }
}
</script>
